import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import {
  Shield,
  Lock,
  FileCheck,
  Server,
  Zap,
  TrendingUp,
  PiggyBank,
  BarChart3,
  Building2,
  Code,
  ChevronRight,
  Menu,
  X,
  Sun,
  Moon,
  Bitcoin,
  Gamepad2,
  Globe,
  ShoppingCart,
  Laptop,
  CreditCard,
  CheckCircle,
  ArrowRight,
  Mail,
  AlertTriangle,
  Eye,
  Settings,
  Users,
  Store,
  ArrowDownCircle,
  ArrowUpCircle,
  Wallet,
  Activity,
  Layers,
} from 'lucide-react';

// Premium stock images
const IMAGES = {
  heroBackground: 'https://images.pexels.com/photos/3912976/pexels-photo-3912976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
  heroCorporate: 'https://images.pexels.com/photos/8067970/pexels-photo-8067970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
  abstractBlue: 'https://images.pexels.com/photos/28428591/pexels-photo-28428591.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  dataCenter: 'https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  serverRoom: 'https://images.unsplash.com/flagged/photo-1579274216947-86eaa4b00475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjBkYXRhJTIwY2VudGVyJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzIxNzk1MjR8MA&ixlib=rb-4.1.0&q=85',
  analytics: 'https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  businessPro: 'https://images.pexels.com/photos/7875856/pexels-photo-7875856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  abstractCube: 'https://images.pexels.com/photos/28428592/pexels-photo-28428592.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  neonAbstract: 'https://images.unsplash.com/photo-1759735541630-036eefb7cd3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBibHVlJTIwbmVvbiUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MjE3OTUyMnww&ixlib=rb-4.1.0&q=85',
  conferenceRoom: 'https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxmaW50ZWNoJTIwZXhlY3V0aXZlJTIwcHJvZmVzc2lvbmFsJTIwbGFwdG9wJTIwZGFyayUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzIxNzk4MjF8MA&ixlib=rb-4.1.0&q=85',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const b = branding || {};
  const heroTitle = b.hero_title || 'Enterprise Payment Infrastructure for Modern Businesses';
  const heroSubtitle = b.hero_subtitle || 'Pay In. Pay Out. Scale Faster.';
  const heroDescription = b.hero_description || 'Accept payments, manage merchant balances and send payouts through a unified payment infrastructure built for modern businesses.';
  const dailyVolume = b.daily_volume_display || '₹50 Cr+';
  const successRate = b.success_rate_display || '99.99%';
  const settlement = b.settlement_display || 'T+0';
  const brandName = b.brand_name || 'AwadhPay';
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Solutions', href: '#services' },
    { label: 'Industries', href: '#industries' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  const trustFeatures = [
    {
      icon: Zap,
      title: 'Advanced Risk Intelligence',
      desc: 'AI-powered risk scoring that adapts to your business model and transaction patterns in real-time.',
    },
    {
      icon: Lock,
      title: 'Secure & Encrypted Infrastructure',
      desc: 'Bank-grade AES-256 encryption with TLS 1.3 for all data in transit and at rest.',
    },
    {
      icon: CheckCircle,
      title: 'High Approval Capability',
      desc: 'Optimized routing and smart retry logic to maximize transaction success rates.',
    },
  ];

  const platformFeatures = [
    { icon: Zap, title: 'Smart Risk Scoring Engine', desc: 'Automated merchant classification based on industry and volume' },
    { icon: PiggyBank, title: 'Rolling Reserve Management', desc: 'Transparent reserve tracking with configurable release schedules' },
    { icon: BarChart3, title: 'Real-Time Settlement Tracking', desc: 'T+2 to T+7 settlements with live status and UTR updates' },
    { icon: AlertTriangle, title: 'Chargeback Monitoring', desc: 'Proactive alerts and dispute management dashboard' },
    { icon: TrendingUp, title: 'Merchant Dashboard Analytics', desc: 'Comprehensive insights into volume, success rates, and trends' },
    { icon: Code, title: 'API-First Infrastructure', desc: 'RESTful APIs with webhooks, idempotency, and full documentation' },
  ];

  const industries = [
    { icon: Bitcoin, title: 'Crypto & Digital Assets', desc: 'Secure fiat on-ramps for exchanges and wallets', image: IMAGES.abstractCube },
    { icon: TrendingUp, title: 'Forex & Trading Platforms', desc: 'Payment solutions for trading educators and brokers', image: IMAGES.analytics },
    { icon: Laptop, title: 'SaaS & Subscription', desc: 'Recurring billing infrastructure for software businesses', image: IMAGES.heroDashboard },
    { icon: Store, title: 'Marketplaces', desc: 'Split payments and escrow for multi-vendor platforms', image: IMAGES.businessPro },
    { icon: Gamepad2, title: 'Gaming Platforms', desc: 'In-game purchases and tournament prize payouts', image: IMAGES.neonAbstract },
    { icon: ShoppingCart, title: 'High-Risk E-commerce', desc: 'Specialized checkout for nutraceuticals and supplements', image: IMAGES.dataCenter },
  ];

  const steps = [
    { num: '01', title: 'Submit Merchant Application', desc: 'Complete our streamlined KYC form with business details and required documents.' },
    { num: '02', title: 'Risk Review & Configuration', desc: 'Our team assesses your application and assigns optimal risk parameters.' },
    { num: '03', title: 'Start Accepting INR Payments', desc: 'Integrate our API and go live within 24-48 hours of approval.' },
  ];

  const securityFeatures = [
    { icon: Users, title: 'Role-Based Access', desc: 'Granular permissions for admins, risk managers, and finance teams' },
    { icon: FileCheck, title: 'Audit Logs', desc: 'Complete traceability of all actions with timestamps' },
    { icon: AlertTriangle, title: 'Risk Alerts', desc: 'Real-time notifications for suspicious activity' },
    { icon: Lock, title: 'Encrypted Data', desc: 'AES-256 encryption for all sensitive information' },
    { icon: Settings, title: 'Settlement Controls', desc: 'Configurable hold and release mechanisms' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Navbar - Keeping existing structure */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-background/90 backdrop-blur-xl border-b border-border/50' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-primary/20 flex items-center justify-center border border-primary/30">
                <CreditCard className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xl font-heading font-bold tracking-tight">
                Awadh<span className="text-primary">Pay</span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-sm">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button variant="outline" onClick={() => navigate('/login')} className="rounded-sm" data-testid="nav-login-btn">
                Login
              </Button>
              <Button onClick={() => navigate('/login')} className="rounded-sm glow-primary" data-testid="nav-get-started-btn">
                Get Started
              </Button>
            </div>

            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-background border-b border-border">
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <a key={item.label} href={item.href} className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground" onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
              <div className="pt-4 flex flex-col gap-2">
                <Button variant="outline" onClick={() => navigate('/login')} className="w-full rounded-sm">Login</Button>
                <Button onClick={() => navigate('/login')} className="w-full rounded-sm">Get Started</Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* SECTION 1: HERO - Reduced height by 30% */}
      <section className="relative pt-24 pb-12 lg:pt-32 lg:pb-20 overflow-hidden min-h-[70vh] flex items-center">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.heroCorporate}
            alt="Professional business team"
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* 45% dark overlay for text readability */}
          <div className="absolute inset-0 bg-background/[0.45]" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30 text-primary text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Enterprise Payment Infrastructure
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold tracking-tight leading-[1.1] mb-4" data-testid="hero-title">
                {heroTitle}
              </h1>
              <p className="text-2xl lg:text-3xl font-heading font-bold mb-5" data-testid="hero-subtitle">
                <span className="text-primary">Pay In.</span> <span className="text-primary">Pay Out.</span> Scale Faster.
              </p>
              
              <p className="text-base lg:text-lg text-muted-foreground mb-4 max-w-lg mx-auto lg:mx-0 leading-relaxed" data-testid="hero-description">
                {heroDescription}
              </p>
              <p className="text-sm text-muted-foreground/80 mb-7 max-w-lg mx-auto lg:mx-0">
                Designed for scalable Payin and Payout operations with intelligent risk controls, real-time visibility and enterprise-grade infrastructure.
              </p>

              {/* Service badges */}
              <div className="flex flex-wrap gap-3 mb-7 justify-center lg:justify-start">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/25 text-sm font-medium">
                  <ArrowDownCircle className="w-4 h-4 text-primary" /> Payin · Payment Collection
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/25 text-sm font-medium">
                  <ArrowUpCircle className="w-4 h-4 text-primary" /> Payout · Money Movement
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" onClick={() => navigate('/login')} className="rounded-sm glow-primary text-base h-11 px-7" data-testid="hero-start-btn">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/login')} className="rounded-sm text-base h-11 px-7 bg-background/50 backdrop-blur-sm" data-testid="hero-login-btn">
                  Login
                </Button>
              </div>

              {/* Trust points */}
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 justify-center lg:justify-start">
                <div className="flex items-center gap-2 text-sm text-foreground/80">
                  <CheckCircle className="w-4 h-4 text-green-500" /><span>Payin + Payout Infrastructure</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground/80">
                  <Lock className="w-4 h-4 text-green-500" /><span>Enterprise Security</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground/80">
                  <Shield className="w-4 h-4 text-green-500" /><span>Intelligent Risk Controls</span>
                </div>
              </div>
            </div>

            {/* Right: Floating Stats Cards */}
            <div className="hidden lg:flex flex-col items-end gap-4">
              <div className="bg-card/90 backdrop-blur-md border border-border/50 rounded-lg p-5 shadow-2xl max-w-xs" data-testid="hero-metric-card">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/20 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Daily Volume</p>
                    <p className="text-2xl font-bold font-mono" data-testid="hero-daily-volume">{dailyVolume}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/50 rounded-sm p-3">
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                    <p className="text-lg font-bold font-mono text-green-500" data-testid="hero-success-rate">{successRate}</p>
                  </div>
                  <div className="bg-muted/50 rounded-sm p-3">
                    <p className="text-xs text-muted-foreground">Settlement</p>
                    <p className="text-lg font-bold font-mono" data-testid="hero-settlement">{settlement}</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground/70 mt-3">Settlement T+0 for eligible merchants and supported payment flows.</p>
              </div>
              
              {/* Infrastructure Status Card */}
              <div className="bg-card/90 backdrop-blur-md border border-green-500/30 rounded-lg p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-green-500/20 flex items-center justify-center">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Infrastructure Status</p>
                    <p className="text-sm font-semibold text-green-500">● Operational</p>
                    <p className="text-[11px] text-muted-foreground">Secure • Scalable • Monitored</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1B: PAYIN + PAYOUT SERVICES */}
      <section id="services" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
              One Platform. <span className="text-primary">Two Payment Flows.</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to collect payments and manage payouts through one unified infrastructure.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { id: 'payin', icon: ArrowDownCircle, title: 'Payment Collection', tag: 'Payin',
                desc: 'Accept and manage customer payments through supported payment methods with unified transaction visibility.',
                pts: ['Payment collection', 'Transaction tracking', 'Webhook notifications', 'Reconciliation', 'Risk monitoring', 'Merchant analytics'], cta: 'Explore Payin' },
              { id: 'payout', icon: ArrowUpCircle, title: 'Payout Infrastructure', tag: 'Payout',
                desc: 'Send payouts to customers, vendors and beneficiaries through a controlled merchant payout workflow.',
                pts: ['Bank payouts', 'Beneficiary management', 'Wallet-based payout processing', 'Admin approval controls', 'Transaction tracking', 'Reconciliation'], cta: 'Explore Payout' },
            ].map((c) => (
              <Card key={c.id} className="border-border/60 hover:border-primary/40 hover:-translate-y-1 transition-all duration-300 shadow-lg" data-testid={`service-card-${c.id}`}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30">
                      <c.icon className="w-6 h-6" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-primary/10 text-primary">{c.tag}</span>
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-2">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mb-5">{c.desc}</p>
                  <ul className="grid grid-cols-2 gap-2 mb-6">
                    {c.pts.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-primary shrink-0" />{p}</li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/login')} data-testid={`service-cta-${c.id}`}>
                    {c.cta} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 1C: VALUE PROPOSITION */}
      <section className="py-16 lg:py-24 bg-muted/20 border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">Built for High-Scale Payment Operations</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {brandName} brings payment collection and payout operations together in one platform, giving businesses greater visibility, control and scalability across their financial workflows.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Layers, title: 'Unified Infrastructure', desc: 'Manage Payin and Payout operations from one platform.' },
              { icon: Activity, title: 'Real-Time Visibility', desc: 'Track transactions, wallet activity and payout status from a centralized dashboard.' },
              { icon: Shield, title: 'Intelligent Risk Controls', desc: 'Monitor transactions and configure controls designed for responsible payment operations.' },
              { icon: TrendingUp, title: 'Enterprise Scalability', desc: 'Infrastructure designed to support growing transaction volumes and operational requirements.' },
            ].map((f) => (
              <Card key={f.title} className="border-border/60 hover:border-primary/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-primary" /></div>
                  <h3 className="font-heading font-semibold mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 1D: STATS STRIP */}
      <section className="py-14 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              [dailyVolume, 'Daily Processing Capacity*'],
              [successRate, 'Target / Platform Success Rate*'],
              [settlement, 'Settlement for Eligible Flows*'],
              ['24/7', 'Platform Monitoring'],
            ].map(([v, l]) => (
              <div key={l} className="text-center rounded-xl border border-border/60 bg-card/50 p-6">
                <p className="text-3xl lg:text-4xl font-heading font-bold text-primary mb-1">{v}</p>
                <p className="text-xs text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground/70 text-center mt-4 max-w-3xl mx-auto">
            *Metrics and service availability may vary by merchant, payment method, risk profile and applicable operational conditions.
          </p>
        </div>
      </section>

      {/* SECTION 2: TRUST & POSITIONING */}
      <section className="py-20 lg:py-28 bg-muted/30 border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
              Built for Complex & High-Risk Industries
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Where traditional payment processors see risk, we see opportunity to build reliable infrastructure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {trustFeatures.map((feature, index) => (
              <Card key={index} className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center mb-6 group-hover:from-primary/30 group-hover:to-purple-500/30 transition-colors">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: PLATFORM FEATURES */}
      <section id="features" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
              Platform <span className="text-gradient">Features</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to manage high-risk payments at scale.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => (
              <Card key={index} className="card-grid group hover:border-primary/30 hover:-translate-y-1 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: INDUSTRIES */}
      <section id="industries" className="py-20 lg:py-28 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
              Payment Infrastructure for <span className="text-primary">Modern Industries</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Flexible Payin and Payout infrastructure for businesses operating across complex payment environments.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-2">Subject to onboarding, compliance and risk assessment.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <Card key={index} className="card-grid overflow-hidden group hover:border-primary/30 transition-all duration-300">
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={industry.image} 
                    alt={industry.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 rounded-sm bg-primary/20 backdrop-blur-sm flex items-center justify-center border border-primary/30">
                      <industry.icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-heading font-semibold mb-2">{industry.title}</h3>
                  <p className="text-sm text-muted-foreground">{industry.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: HOW IT WORKS */}
      <section id="about" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
              How It <span className="text-gradient">Works</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get started in three simple steps and go live within 48 hours.
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-24 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/30 mb-6 relative z-10">
                    <span className="text-2xl font-heading font-bold text-primary">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: SECURITY & CONTROL */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={IMAGES.serverRoom} 
            alt="Data Center" 
            className="w-full h-full object-cover opacity-10"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4">
                Security & <span className="text-gradient">Control</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Enterprise-grade security with complete operational control. Built for compliance-first organizations.
              </p>
              
              <div className="space-y-4">
                {securityFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-colors">
                    <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{feature.title}</h4>
                      <p className="text-sm text-muted-foreground">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-xl overflow-hidden border border-border/50 shadow-2xl">
                <img 
                  src={IMAGES.dataCenter} 
                  alt="Secure Infrastructure" 
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: FINAL CTA */}
      <section id="pricing" className="py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.abstractBlue} 
            alt="Abstract Background" 
            className="w-full h-full object-cover opacity-20"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-background" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold mb-6">
            Ready to Power Your High-Risk Growth?
          </h2>
          <p className="text-lg lg:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            Join forward-thinking businesses that trust AwadhPay for their payment infrastructure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/login')} className="rounded-sm glow-primary text-base h-12 px-8" data-testid="cta-get-started-btn">
              Start Onboarding
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/login')} className="rounded-sm text-base h-12 px-8">
              Login to Dashboard
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="bg-card border-t border-border/50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-sm bg-primary/20 flex items-center justify-center border border-primary/30">
                  <CreditCard className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xl font-heading font-bold">
                  Awadh<span className="text-primary">Pay</span>
                </span>
              </Link>
              <p className="text-sm text-muted-foreground mb-4">
                Enterprise payment infrastructure for Payin, Payout and scalable business operations.
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>support@awadhpay.com</span>
              </div>
            </div>

            <div>
              <h4 className="font-heading font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                {['About Us', 'Careers', 'Blog', 'Press', 'Partners'].map((item) => (
                  <li key={item}>
                    <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                {['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'AML Policy', 'Refund Policy'].map((item) => (
                  <li key={item}>
                    <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                {['Documentation', 'API Reference', 'Status', 'Contact Us'].map((item) => (
                  <li key={item}>
                    <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 AwadhPay. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Built for compliant payment operations through authorized partners.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
